import { createColumnHelper } from '@tanstack/table-core';
import { useMemo } from 'react';
import { FiInfo } from 'react-icons/fi';

import { NativeTable } from '@/components/nativeTable/nativeTable';
import { useNativeTable } from '@/components/nativeTable/useNativeTable';
import { Subsection } from '@/components/section/section';
import { buildColumnClassString } from '@/components/table/table';
import { Tooltip } from '@/components/tooltip/tooltip';
import { ModelUsageSummary } from '@/types/observability';

import styles from './modelUsageSection.module.scss';

const modelUsageColumnHelper = createColumnHelper<ModelUsageSummary>();

function ModelUsageTable( { models }: { models: ModelUsageSummary[] } ) {
  const columns = useMemo( () => [
    modelUsageColumnHelper.accessor( 'model', {
      header: 'Model Name',
      size: 500,
      cell: info => (
        <Tooltip content={ info.getValue() } position="top">
          <span className={ styles.modelNameCell }>{ info.getValue() }</span>
        </Tooltip>
      ),
      meta: { classStr: buildColumnClassString( { width: 3 } ) }
    } ),
    modelUsageColumnHelper.accessor( 'distribution_percentage', {
      header: 'Model Distribution %',
      size: 50,
      cell: info => {
        const pct = info.getValue();
        return (
          <div className={ styles.distributionCell }>
            <span className={ styles.distributionPercentage }>{ pct.toFixed( 1 ) }%</span>
            <div className={ styles.distributionBar }>
              <div className={ styles.distributionFill } style={ { width: `${ Math.min( pct, 100 ) }%` } } />
            </div>
          </div>
        );
      },
      meta: { classStr: buildColumnClassString( { width: 2 } ) }
    } ),
    modelUsageColumnHelper.accessor( 'prompt_to_completion_ratio', {
      header: () => (
        <Tooltip content="Ratio of completion tokens to prompt tokens for this agent's model usage." position="top">
          <span className={ styles.columnHeaderWithIcon }>
            Prompt-to-Completion <FiInfo className={ styles.infoIcon } />
          </span>
        </Tooltip>
      ),
      size: 50,
      cell: info => info.getValue().toFixed( 2 ),
      meta: { classStr: buildColumnClassString( { width: 2 } ) }
    } )
  ], [] );

  const table = useNativeTable( { data: models, columns, enableSorting: false } );

  return <NativeTable table={ table } pagination={ false } tbodyTestId="table-body-model-usage-table" />;
}

export function ModelUsageSection( { models, timeRangeLabel }: {
  models: ModelUsageSummary[];
  timeRangeLabel: string;
} ) {
  return (
    <Subsection testId="analytics-model-usage" title={ `Model Usage ${ timeRangeLabel }` }>
      { models.length === 0
        ? <p className={ styles.noData }>No model usage data for this period.</p>
        : <ModelUsageTable models={ models } />}
    </Subsection>
  );
}
